import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import API_BASE_URL from '../config';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Link,
  Rating,
  Box,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemIcon,
  Modal,
  Paper,
  Chip,
  Tabs,
  Tab,
  LinearProgress,
  Fade,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  Celebration,
  EmojiEvents,
  Add,
  MenuBook,
  Star,
  AutoStories,
  Schedule,
  Search,
  FilterList,
  Sort,
  Close,
} from '@mui/icons-material';
import Lottie from 'react-lottie-player';

import congratsAnimation from './congratsAnimation.json';

function BookList() {
  const [myBooks, setMyBooks] = useState({
    wantToRead: [],
    currentlyReading: [],
    finished: [],
    dropped: [],
  });
  const [challenges, setChallenges] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openChallengeDialog, setOpenChallengeDialog] = useState(false);
  const [targetDate, setTargetDate] = useState('');
  const [challengeDescription, setChallengeDescription] = useState('');
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [selectedChallengeBooks, setSelectedChallengeBooks] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [showCongratsModal, setShowCongratsModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('title');
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedRating, setSelectedRating] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchBooksAndChallenges = async () => {
      try {
        const booksRes = await axios.get(`${API_BASE_URL}/books/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const books = booksRes.data;
        setMyBooks({
          wantToRead: books.filter((book) => book.status === 'want to read'),
          currentlyReading: books.filter((book) => book.status === 'currently reading'),
          finished: books.filter((book) => book.status === 'finished'),
          dropped: books.filter((book) => book.status === 'dropped'),
        });

        const challengesRes = await axios.get(`${API_BASE_URL}/challenges/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const fetchedChallenges = challengesRes.data;
        setChallenges(fetchedChallenges);
        checkForCompletedChallenges(fetchedChallenges, books);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchBooksAndChallenges();
  }, [token]);

  const checkForCompletedChallenges = (fetchedChallenges, books) => {
    // Check for newly completed challenges using backend status
    for (const challenge of fetchedChallenges) {
      // Use backend's completion tracking
      const isCompleted = challenge.status === 'completed' || challenge.booksCompleted >= challenge.targetCount;

      // Only show congrats modal once per challenge completion
      if (isCompleted && !localStorage.getItem(`challenge_celebrated_${challenge._id}`)) {
        setSelectedChallenge(challenge);

        // Use book data from challenge (backend already tracks this)
        const booksInChallenge = challenge.books.map((bookEntry) => {
          return {
            title: bookEntry.title || 'Untitled Book',
            authors: bookEntry.authors?.length > 0 ? bookEntry.authors : ['Unknown Author'],
            isFinished: bookEntry.isCompleted || false,
            thumbnail: bookEntry.thumbnail || '',
          };
        });

        setSelectedChallengeBooks(booksInChallenge);
        setShowCongratsModal(true);
        // Mark as celebrated to prevent showing modal again
        localStorage.setItem(`challenge_celebrated_${challenge._id}`, 'true');
        break;
      }
    }
  };

  const handleBookSelection = (bookId) => {
    setSelectedBooks((prevSelected) =>
      prevSelected.includes(bookId)
        ? prevSelected.filter((id) => id !== bookId)
        : [...prevSelected, bookId]
    );
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedBooks([]);
    setTargetDate('');
    setChallengeDescription('');
  };

  const handleAddChallenge = async () => {
    if (targetDate && selectedBooks.length > 0) {
      try {
        const newChallenge = {
          type: 'read_books',
          description: challengeDescription || `Read ${selectedBooks.length} books by ${new Date(targetDate).toLocaleDateString()}`,
          targetCount: selectedBooks.length,
          targetDate,
          books: selectedBooks, // Backend expects array of bookIds
          autoSync: true,
        };

        const res = await axios.post(`${API_BASE_URL}/challenges/add`, newChallenge, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setChallenges([...challenges, res.data]);
        handleCloseDialog();
      } catch (err) {
        console.error('Error adding challenge:', err);
        alert('Failed to create challenge. Please try again.');
      }
    }
  };

  const handleOpenChallengeDialog = (challenge) => {
    setSelectedChallenge(challenge);

    // Use backend's book completion tracking instead of manual checking
    const booksInChallenge = challenge.books.map((bookEntry) => {
      return {
        bookId: bookEntry.bookId,
        title: bookEntry.title || 'Untitled Book',
        authors: bookEntry.authors && bookEntry.authors.length > 0 ? bookEntry.authors : ['Unknown Author'],
        isFinished: bookEntry.isCompleted || false, // Use backend's isCompleted field
        progress: bookEntry.progress || 0,
      };
    });

    setSelectedChallengeBooks(booksInChallenge);

    // Check if challenge is completed and show modal if not yet celebrated
    const isCompleted = challenge.status === 'completed' || challenge.booksCompleted >= challenge.targetCount;
    if (isCompleted && !localStorage.getItem(`challenge_celebrated_${challenge._id}`)) {
      setShowCongratsModal(true);
      localStorage.setItem(`challenge_celebrated_${challenge._id}`, 'true');
    }

    setOpenChallengeDialog(true);
  };

  const handleCloseChallengeDialog = () => {
    setOpenChallengeDialog(false);
    setSelectedChallenge(null);
    setSelectedChallengeBooks([]);
  };

  const handleCloseCongratsModal = async () => {
    // Keep completed challenges instead of deleting them
    // This preserves user's achievement history

    setShowCongratsModal(false);
    setSelectedChallenge(null);
    setSelectedChallengeBooks([]);
  };

  const handleToggleBookCompletion = async (bookId, currentProgress) => {
    if (!selectedChallenge) return;

    // Toggle: if book is at 100%, set to 0%, otherwise set to 100%
    const newProgress = currentProgress >= 100 ? 0 : 100;
    const isCompleted = newProgress >= 100;

    // OPTIMISTIC UPDATE - Update UI immediately for instant feedback
    const updatedBooks = selectedChallengeBooks.map((book) => {
      if (book.bookId === bookId) {
        return {
          ...book,
          progress: newProgress,
          isFinished: isCompleted,
        };
      }
      return book;
    });
    setSelectedChallengeBooks(updatedBooks);

    try {
      // Make API call in background
      await axios.patch(
        `${API_BASE_URL}/challenges/update-book-progress/${selectedChallenge._id}/${bookId}`,
        { progress: newProgress },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Refresh challenges to get updated data
      const challengesRes = await axios.get(`${API_BASE_URL}/challenges/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChallenges(challengesRes.data);

      // Update the selectedChallenge with fresh data from server
      const updatedChallenge = challengesRes.data.find(c => c._id === selectedChallenge._id);
      if (updatedChallenge) {
        setSelectedChallenge(updatedChallenge);

        // Sync with server data
        const booksInChallenge = updatedChallenge.books.map((bookEntry) => {
          return {
            bookId: bookEntry.bookId,
            title: bookEntry.title || 'Untitled Book',
            authors: bookEntry.authors && bookEntry.authors.length > 0 ? bookEntry.authors : ['Unknown Author'],
            isFinished: bookEntry.isCompleted || false,
            progress: bookEntry.progress || 0,
          };
        });
        setSelectedChallengeBooks(booksInChallenge);

        // Check if challenge is now completed and show congratulations modal
        const isChallengeCompleted = updatedChallenge.status === 'completed' ||
                                    updatedChallenge.booksCompleted >= updatedChallenge.targetCount;

        if (isChallengeCompleted && !localStorage.getItem(`challenge_celebrated_${updatedChallenge._id}`)) {
          // Close the challenge details modal
          setOpenChallengeDialog(false);

          // Show congratulations modal
          setShowCongratsModal(true);
          localStorage.setItem(`challenge_celebrated_${updatedChallenge._id}`, 'true');
        }
      }
    } catch (err) {
      console.error('Error toggling book completion:', err);
      // Revert optimistic update on error
      setSelectedChallengeBooks(selectedChallengeBooks);
      alert('Failed to update book status. Please try again.');
    }
  };

  const handleDeleteChallenge = async () => {
    if (!selectedChallenge) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete this challenge? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE_URL}/challenges/${selectedChallenge._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Remove from local state
      setChallenges(challenges.filter(c => c._id !== selectedChallenge._id));

      // Close the modal
      handleCloseChallengeDialog();
    } catch (err) {
      console.error('Error deleting challenge:', err);
      alert('Failed to delete challenge. Please try again.');
    }
  };

  const totalBooks = Object.values(myBooks).flat().length;
  const avgRating = (() => {
    const allBooks = Object.values(myBooks).flat();
    const ratedBooks = allBooks.filter(book => book.rating > 0);
    if (ratedBooks.length === 0) return 0;
    return (ratedBooks.reduce((sum, book) => sum + book.rating, 0) / ratedBooks.length).toFixed(1);
  })();

  const getTabContent = () => {
    switch (activeTab) {
      case 0:
        return myBooks.currentlyReading;
      case 1:
        return myBooks.wantToRead;
      case 2:
        return myBooks.finished;
      case 3:
        return myBooks.dropped;
      default:
        return [];
    }
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case 0:
        return 'Currently Reading';
      case 1:
        return 'Want To Read';
      case 2:
        return 'Finished Books';
      case 3:
        return 'Dropped Books';
      default:
        return '';
    }
  };

  // Get all unique genres from all books
  const getAllGenres = () => {
    const allBooks = Object.values(myBooks).flat();
    const genresSet = new Set();
    allBooks.forEach(book => {
      if (book.genres && Array.isArray(book.genres)) {
        book.genres.forEach(genre => genresSet.add(genre));
      }
    });
    return Array.from(genresSet).sort();
  };

  // Filter, search, and sort books
  const getFilteredAndSortedBooks = (books) => {
    let filteredBooks = [...books];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredBooks = filteredBooks.filter(book =>
        book.title.toLowerCase().includes(query) ||
        (book.authors && book.authors.some(author => author.toLowerCase().includes(query)))
      );
    }

    // Apply genre filter
    if (selectedGenres.length > 0) {
      filteredBooks = filteredBooks.filter(book =>
        book.genres && book.genres.some(genre => selectedGenres.includes(genre))
      );
    }

    // Apply rating filter
    if (selectedRating !== null) {
      filteredBooks = filteredBooks.filter(book =>
        book.rating >= selectedRating
      );
    }

    // Apply sort
    filteredBooks.sort((a, b) => {
      switch (sortOption) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'title-desc':
          return b.title.localeCompare(a.title);
        case 'author':
          const authorA = a.authors?.[0] || '';
          const authorB = b.authors?.[0] || '';
          return authorA.localeCompare(authorB);
        case 'rating-high':
          return (b.rating || 0) - (a.rating || 0);
        case 'rating-low':
          return (a.rating || 0) - (b.rating || 0);
        case 'date-added':
          return new Date(b.dateAdded || 0) - new Date(a.dateAdded || 0);
        case 'date-added-old':
          return new Date(a.dateAdded || 0) - new Date(b.dateAdded || 0);
        default:
          return 0;
      }
    });

    return filteredBooks;
  };

  const handleGenreToggle = (genre) => {
    setSelectedGenres(prev =>
      prev.includes(genre)
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedGenres([]);
    setSelectedRating(null);
    setSortOption('title');
  };

  return (
    <Box sx={{ backgroundColor: '#FAF3E0', minHeight: '100vh', pb: 6 }}>
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Header */}
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#333', mb: 3, textAlign: 'center' }}>
          My Book Collection
        </Typography>

        {/* Stats Summary */}
        <Fade in timeout={600}>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={3}>
              <Box
                sx={{
                  p: 2,
                  textAlign: 'center',
                  backgroundColor: 'transparent',
                  borderRadius: 2,
                }}
              >
                <MenuBook sx={{ fontSize: 28, color: '#8D7B68', mb: 0.5 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
                  {totalBooks}
                </Typography>
                <Typography variant="caption" sx={{ color: '#555' }}>
                  Total Books
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box
                sx={{
                  p: 2,
                  textAlign: 'center',
                  backgroundColor: 'transparent',
                  borderRadius: 2,
                }}
              >
                <CheckCircle sx={{ fontSize: 28, color: '#4CAF50', mb: 0.5 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
                  {myBooks.finished.length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#555' }}>
                  Finished
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box
                sx={{
                  p: 2,
                  textAlign: 'center',
                  backgroundColor: 'transparent',
                  borderRadius: 2,
                }}
              >
                <Star sx={{ fontSize: 28, color: '#F4B400', mb: 0.5 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
                  {avgRating > 0 ? avgRating : '-'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#555' }}>
                  Avg Rating
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box
                sx={{
                  p: 2,
                  textAlign: 'center',
                  backgroundColor: 'transparent',
                  borderRadius: 2,
                }}
              >
                <AutoStories sx={{ fontSize: 28, color: '#8D7B68', mb: 0.5 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
                  {myBooks.currentlyReading.length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#555' }}>
                  Reading Now
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Fade>

        <Grid container spacing={3}>
          {/* Sidebar - Challenges */}
          <Grid item xs={12} md={3}>
            <Box
              sx={{
                p: 2.5,
                backgroundColor: 'transparent',
                borderRadius: 2,
                border: '2px solid #8D7B68',
                position: { md: 'sticky' },
                top: { md: 100 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmojiEvents sx={{ color: '#8D7B68', mr: 1, fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                  Challenges
                </Typography>
              </Box>

              <Button
                variant="contained"
                fullWidth
                startIcon={<Add />}
                onClick={handleOpenDialog}
                sx={{
                  backgroundColor: '#8D7B68',
                  '&:hover': { backgroundColor: '#7A6859' },
                  color: '#fff',
                  borderRadius: 2,
                  mb: 2,
                  textTransform: 'none',
                  py: 1,
                }}
              >
                Create Challenge
              </Button>

              {challenges.filter(c => c.status !== 'completed').length === 0 ? (
                <Typography variant="body2" sx={{ color: '#555', textAlign: 'center', py: 3 }}>
                  No active challenges yet
                </Typography>
              ) : (
                <List sx={{ p: 0 }}>
                  {challenges.filter(c => c.status !== 'completed').map((challenge) => {
                    const progress = challenge.completionPercentage || 0;
                    return (
                      <Box
                        key={challenge._id}
                        sx={{
                          p: 2,
                          mb: 1.5,
                          backgroundColor: 'rgba(141, 123, 104, 0.1)',
                          border: '1px solid #8D7B68',
                          borderRadius: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: '#8D7B68',
                            '& .MuiTypography-root': { color: '#fff' },
                            '& .MuiLinearProgress-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            },
                            '& .MuiSvgIcon-root': { color: '#fff' },
                          },
                        }}
                        onClick={() => handleOpenChallengeDialog(challenge)}
                      >
                        {challenge.description && (
                          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
                            {challenge.description}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#333' }}>
                          {challenge.booksCompleted} / {challenge.targetCount} books
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#DDD',
                            mb: 0.5,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: '#8D7B68',
                              borderRadius: 3,
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Schedule sx={{ fontSize: 14, color: '#555', mr: 0.5 }} />
                            <Typography variant="caption" sx={{ color: '#555' }}>
                              Due: {new Date(challenge.targetDate).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Chip
                            label={`${progress}%`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              backgroundColor: progress >= 100 ? '#4CAF50' : '#8D7B68',
                              color: '#fff',
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </List>
              )}
            </Box>
          </Grid>

          {/* Main Content - Books */}
          <Grid item xs={12} md={9}>
            <Box
              sx={{
                backgroundColor: 'transparent',
              }}
            >
              {/* Tabs */}
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{
                  mb: 3,
                  backgroundColor: 'transparent',
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#777',
                    minHeight: 48,
                    transition: 'all 0.2s',
                  },
                  '& .Mui-selected': {
                    color: '#8D7B68',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#8D7B68',
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                  },
                }}
              >
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AutoStories sx={{ fontSize: 20 }} />
                      <span>Currently Reading</span>
                      <Chip
                        label={myBooks.currentlyReading.length}
                        size="small"
                        sx={{ height: 20, fontSize: '0.75rem' }}
                      />
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Want to Read</span>
                      <Chip
                        label={myBooks.wantToRead.length}
                        size="small"
                        sx={{ height: 20, fontSize: '0.75rem' }}
                      />
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Finished</span>
                      <Chip
                        label={myBooks.finished.length}
                        size="small"
                        sx={{ height: 20, fontSize: '0.75rem' }}
                      />
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Dropped</span>
                      <Chip
                        label={myBooks.dropped.length}
                        size="small"
                        sx={{ height: 20, fontSize: '0.75rem' }}
                      />
                    </Box>
                  }
                />
              </Tabs>

              {/* Search, Sort, and Filter Controls */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(141, 123, 104, 0.05)', borderRadius: 2, border: '1px solid rgba(141, 123, 104, 0.2)' }}>
                {/* Search and Sort Row */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by title or author..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: '#8D7B68' }} />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSearchQuery('')}>
                              <Close sx={{ fontSize: 18 }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        backgroundColor: '#fff',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'rgba(141, 123, 104, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: '#8D7B68',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#8D7B68',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Select
                      fullWidth
                      size="small"
                      value={sortOption}
                      onChange={(e) => setSortOption(e.target.value)}
                      startAdornment={
                        <InputAdornment position="start">
                          <Sort sx={{ color: '#8D7B68', ml: 1 }} />
                        </InputAdornment>
                      }
                      sx={{
                        backgroundColor: '#fff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(141, 123, 104, 0.3)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#8D7B68',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#8D7B68',
                        },
                      }}
                    >
                      <MenuItem value="title">Title (A-Z)</MenuItem>
                      <MenuItem value="title-desc">Title (Z-A)</MenuItem>
                      <MenuItem value="author">Author (A-Z)</MenuItem>
                      <MenuItem value="rating-high">Rating (High to Low)</MenuItem>
                      <MenuItem value="rating-low">Rating (Low to High)</MenuItem>
                      <MenuItem value="date-added">Recently Added</MenuItem>
                      <MenuItem value="date-added-old">Oldest First</MenuItem>
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<FilterList />}
                      onClick={() => setShowFilters(!showFilters)}
                      sx={{
                        borderColor: '#8D7B68',
                        color: '#333',
                        backgroundColor: showFilters ? 'rgba(141, 123, 104, 0.1)' : '#fff',
                        '&:hover': {
                          borderColor: '#8D7B68',
                          backgroundColor: 'rgba(141, 123, 104, 0.15)',
                        },
                        textTransform: 'none',
                        fontWeight: 600,
                        height: '40px',
                      }}
                    >
                      Filters {(selectedGenres.length > 0 || selectedRating !== null) && `(${selectedGenres.length + (selectedRating !== null ? 1 : 0)})`}
                    </Button>
                  </Grid>
                </Grid>

                {/* Filter Panel (Collapsible) */}
                <Collapse in={showFilters}>
                  <Box sx={{ pt: 2, borderTop: '1px solid rgba(141, 123, 104, 0.15)' }}>
                    {/* Rating Filter */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
                        Minimum Rating
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <Chip
                            key={rating}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Star sx={{ fontSize: 16 }} />
                                <span>{rating}+</span>
                              </Box>
                            }
                            onClick={() => setSelectedRating(selectedRating === rating ? null : rating)}
                            sx={{
                              backgroundColor: selectedRating === rating ? '#8D7B68' : '#fff',
                              color: selectedRating === rating ? '#fff' : '#333',
                              border: '1px solid #8D7B68',
                              '&:hover': {
                                backgroundColor: selectedRating === rating ? '#7A6859' : 'rgba(141, 123, 104, 0.1)',
                              },
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Box>
                    </Box>

                    {/* Genre Filter */}
                    {getAllGenres().length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
                          Genres
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', maxHeight: '200px', overflowY: 'auto' }}>
                          {getAllGenres().map((genre) => (
                            <Chip
                              key={genre}
                              label={genre}
                              onClick={() => handleGenreToggle(genre)}
                              sx={{
                                backgroundColor: selectedGenres.includes(genre) ? '#8D7B68' : '#fff',
                                color: selectedGenres.includes(genre) ? '#fff' : '#333',
                                border: '1px solid #8D7B68',
                                '&:hover': {
                                  backgroundColor: selectedGenres.includes(genre) ? '#7A6859' : 'rgba(141, 123, 104, 0.1)',
                                },
                                fontWeight: 500,
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Clear Filters Button */}
                    {(searchQuery || selectedGenres.length > 0 || selectedRating !== null) && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Button
                          size="small"
                          onClick={clearAllFilters}
                          sx={{
                            color: '#8D7B68',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                              backgroundColor: 'rgba(141, 123, 104, 0.1)',
                            },
                          }}
                        >
                          Clear All Filters
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>

              {/* Books Grid */}
              <Box sx={{ p: 3 }}>
                {(() => {
                  const filteredBooks = getFilteredAndSortedBooks(getTabContent());
                  if (getTabContent().length === 0) {
                    return (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <MenuBook sx={{ fontSize: 64, color: '#DDD', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#999', mb: 1 }}>
                          No books in "{getTabTitle()}"
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#999' }}>
                          Start adding books to your collection
                        </Typography>
                      </Box>
                    );
                  }
                  if (filteredBooks.length === 0) {
                    return (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Search sx={{ fontSize: 64, color: '#DDD', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#999', mb: 1 }}>
                          No books match your filters
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
                          Try adjusting your search or filters
                        </Typography>
                        <Button
                          onClick={clearAllFilters}
                          variant="outlined"
                          sx={{
                            borderColor: '#8D7B68',
                            color: '#8D7B68',
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: '#8D7B68',
                              backgroundColor: 'rgba(141, 123, 104, 0.1)',
                            },
                          }}
                        >
                          Clear Filters
                        </Button>
                      </Box>
                    );
                  }
                  return (
                    <Grid container spacing={2.5}>
                      {filteredBooks.map((book) => (
                        <Grid item xs={12} sm={6} md={4} key={book._id}>
                          <Link
                            component={RouterLink}
                            to={`/book/${book._id}`}
                            underline="none"
                            sx={{ textDecoration: 'none' }}
                          >
                            <Card
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                backgroundColor: 'transparent',
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: '1px solid #8D7B68',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  backgroundColor: 'rgba(141, 123, 104, 0.05)',
                                  borderColor: '#7A6859',
                                  boxShadow: '0 4px 12px rgba(141, 123, 104, 0.2)',
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  width: '100%',
                                  aspectRatio: '2/3',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: '#fff',
                                  overflow: 'hidden',
                                }}
                              >
                                <CardMedia
                                  component="img"
                                  image={
                                    book.thumbnail
                                      ? book.thumbnail.replace(/^http:/, 'https:').replace(/&amp;/g, '&')
                                      : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="300"%3E%3Crect width="200" height="300" fill="%23F4ECE3"/%3E%3Ctext x="50%25" y="50%25" font-family="Georgia,serif" font-size="18" fill="%238D7B68" text-anchor="middle" dominant-baseline="middle"%3ENo Cover%3C/text%3E%3C/svg%3E'
                                  }
                                  alt={book.title}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                  }}
                                  onError={(e) => {
                                    console.error('Image failed to load:', book.title, book.thumbnail);
                                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="300"%3E%3Crect width="200" height="300" fill="%23F4ECE3"/%3E%3Ctext x="50%25" y="50%25" font-family="Georgia,serif" font-size="18" fill="%238D7B68" text-anchor="middle" dominant-baseline="middle"%3ENo Cover%3C/text%3E%3C/svg%3E';
                                  }}
                                />
                              </Box>
                              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 600,
                                    color: '#333',
                                    mb: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {book.title}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#555',
                                    display: 'block',
                                    mb: 1,
                                  }}
                                >
                                  {book.authors?.join(', ')}
                                </Typography>
                                {book.rating > 0 && (
                                  <Rating
                                    name="read-only"
                                    value={book.rating}
                                    readOnly
                                    size="small"
                                    sx={{
                                      '& .MuiRating-icon': {
                                        color: '#F4B400',
                                      },
                                    }}
                                  />
                                )}
                              </CardContent>
                            </Card>
                          </Link>
                        </Grid>
                      ))}
                    </Grid>
                  );
                })()}
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Create Challenge Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(51, 41, 30, 0.38)',
              backdropFilter: 'blur(1px) saturate(0.95)',
            },
          }}
          PaperProps={{
            sx: {
              background: 'linear-gradient(180deg, #FAF3E0 0%, #F6EDD8 100%)',
              borderRadius: 2,
              boxShadow: 6,
              border: '1px solid rgba(141, 123, 104, 0.25)',
            }
          }}
        >
          <DialogTitle sx={{
            backgroundColor: 'transparent',
            color: '#3b2e22',
            fontFamily: `'Cinzel', 'Trajan Pro', Georgia, serif`,
            fontWeight: 700,
            pb: 2,
            pt: 2,
            fontSize: '1.3rem',
            letterSpacing: 0.2,
            borderBottom: '1px solid rgba(141, 123, 104, 0.25)',
          }}>
            Create New Challenge
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
            <TextField
              label="Challenge Name"
              variant="outlined"
              fullWidth
              size="medium"
              value={challengeDescription}
              onChange={(e) => setChallengeDescription(e.target.value)}
              placeholder="e.g., Summer Reading Marathon"
              sx={{
                mt: 1.5,
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F6EDD8',
                  '& input': {
                    fontFamily: `'Cinzel', 'Trajan Pro', Georgia, serif`,
                  },
                  '& fieldset': {
                    borderColor: 'rgba(141, 123, 104, 0.35)',
                    borderWidth: 1,
                  },
                  '&:hover fieldset': {
                    borderColor: '#8D7B68',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#8D7B68',
                    borderWidth: 1,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#5c5247',
                  fontFamily: `'Cinzel', 'Trajan Pro', Georgia, serif`,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#8D7B68',
                },
              }}
            />
            <TextField
              label="Target Date"
              type="date"
              variant="outlined"
              fullWidth
              size="medium"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F6EDD8',
                  '& input': {
                    fontFamily: `'Cinzel', 'Trajan Pro', Georgia, serif`,
                  },
                  '& fieldset': {
                    borderColor: 'rgba(141, 123, 104, 0.35)',
                    borderWidth: 1,
                  },
                  '&:hover fieldset': {
                    borderColor: '#8D7B68',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#8D7B68',
                    borderWidth: 1,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#5c5247',
                  fontFamily: `'Cinzel', 'Trajan Pro', Georgia, serif`,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#8D7B68',
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, color: '#3b2e22', fontFamily: `'Georgia', 'Times New Roman', serif` }}>
                Select Books
              </Typography>
              {selectedBooks.length > 0 && (
                <Chip
                  label={`${selectedBooks.length} selected`}
                  size="small"
                  sx={{
                    backgroundColor: '#8D7B68',
                    color: '#FFF',
                    fontWeight: 700,
                    height: 26,
                    fontSize: '0.8rem',
                  }}
                />
              )}
            </Box>
            <Box
              sx={{
                maxHeight: 260,
                overflowY: 'auto',
                border: '1px solid rgba(141, 123, 104, 0.25)',
                borderRadius: 2,
                p: 2,
                 backgroundColor: '#F7EEDC',
              }}
            >
              {[...myBooks.wantToRead, ...myBooks.currentlyReading].length === 0 ? (
                <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', py: 3 }}>
                  No books available. Add books to "Want to Read" or "Currently Reading" first.
                </Typography>
              ) : (
                [...myBooks.wantToRead, ...myBooks.currentlyReading].map((book) => (
                  <Box
                    key={book._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      p: 1.5,
                      mb: 1,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      backgroundColor: selectedBooks.includes(book._id) ? 'rgba(141, 123, 104, 0.12)' : 'transparent',
                      '&:hover': {
                        backgroundColor: selectedBooks.includes(book._id) ? 'rgba(141, 123, 104, 0.18)' : 'rgba(141, 123, 104, 0.08)',
                      },
                      '&:last-child': {
                        mb: 0,
                      },
                    }}
                    onClick={() => handleBookSelection(book._id)}
                  >
                    <Checkbox
                      checked={selectedBooks.includes(book._id)}
                      onClick={(e) => { e.stopPropagation(); handleBookSelection(book._id); }}
                      sx={{
                        color: '#8D7B68',
                        '&.Mui-checked': {
                          color: '#8D7B68',
                        },
                        p: 0,
                        mr: 1.5,
                        mt: 0.2,
                      }}
                      inputProps={{ 'aria-label': `select ${book.title}` }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', lineHeight: 1.4, mb: 0.2 }}>
                        {book.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#777', lineHeight: 1.3, fontSize: '0.8rem' }}>
                        {book.authors?.join(', ')}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(141, 123, 104, 0.2)', gap: 1.5 }}>
            <Button
              onClick={handleCloseDialog}
              sx={{
                color: '#666',
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                py: 0.75,
                '&:hover': {
                  backgroundColor: 'rgba(141, 123, 104, 0.08)',
                  color: '#333',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddChallenge}
              variant="contained"
              disabled={!targetDate || selectedBooks.length === 0}
              sx={{
                backgroundColor: '#8D7B68',
                color: '#FAF3E0',
                textTransform: 'none',
                fontWeight: 700,
                px: 3,
                py: 0.75,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#7A6859',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  backgroundColor: '#D5D5D5',
                  color: '#999',
                },
              }}
            >
              Create Challenge
            </Button>
          </DialogActions>
        </Dialog>

        {/* Challenge Details Dialog */}
        <Dialog open={openChallengeDialog} onClose={handleCloseChallengeDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#F4ECE3', fontWeight: 600, borderBottom: '2px solid #8D7B68', pb: 2 }}>
            Challenge Details
          </DialogTitle>
          <DialogContent sx={{ pt: 4, px: 3 }}>
            {selectedChallenge && (
              <Box sx={{ mb: 3 }}>
                {selectedChallenge.description && (
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#333' }}>
                    {selectedChallenge.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Chip
                    icon={<EmojiEvents />}
                    label={`${selectedChallenge.booksCompleted} / ${selectedChallenge.targetCount} books`}
                    sx={{
                      backgroundColor: '#8D7B68',
                      color: '#fff',
                      fontWeight: 600,
                      '& .MuiChip-icon': { color: '#fff' },
                    }}
                  />
                  <Chip
                    icon={<Schedule />}
                    label={`Due: ${new Date(selectedChallenge.targetDate).toLocaleDateString()}`}
                    sx={{
                      backgroundColor: 'rgba(141, 123, 104, 0.2)',
                      color: '#333',
                      fontWeight: 600,
                    }}
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={selectedChallenge.completionPercentage || 0}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: '#DDD',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: selectedChallenge.completionPercentage >= 100 ? '#B8860B' : '#8D7B68',
                        borderRadius: 5,
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#555', mt: 0.5, display: 'block' }}>
                    {selectedChallenge.completionPercentage || 0}% complete
                  </Typography>
                </Box>
              </Box>
            )}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
              Books in this challenge:
            </Typography>
            <List sx={{ p: 0 }}>
              {selectedChallengeBooks.map((book, index) => (
                <ListItem
                  key={index}
                  onClick={() => handleToggleBookCompletion(book.bookId, book.progress || 0)}
                  sx={{
                    backgroundColor: book.isFinished ? 'rgba(184, 134, 11, 0.12)' : '#FAF3E0',
                    borderRadius: 2,
                    mb: 1.5,
                    border: book.isFinished ? '2px solid #B8860B' : '1px solid #DDD',
                    transition: 'all 0.15s',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: book.isFinished ? 'rgba(184, 134, 11, 0.18)' : 'rgba(141, 123, 104, 0.08)',
                      transform: 'scale(1.01)',
                    },
                  }}
                >
                  <ListItemIcon>
                    {book.isFinished ? (
                      <CheckCircle sx={{ color: '#B8860B', fontSize: 28 }} />
                    ) : (
                      <RadioButtonUnchecked sx={{ color: '#999', fontSize: 28 }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={book.title || 'Untitled Book'}
                    secondary={book.authors?.join(', ') || 'Unknown Author'}
                    primaryTypographyProps={{ fontWeight: 600, color: '#333' }}
                    secondaryTypographyProps={{ color: '#555' }}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, borderTop: '1px solid #DDD', display: 'flex', justifyContent: 'space-between' }}>
            <Button
              onClick={handleDeleteChallenge}
              variant="outlined"
              sx={{
                borderColor: '#d32f2f',
                color: '#d32f2f',
                '&:hover': {
                  borderColor: '#b71c1c',
                  backgroundColor: 'rgba(211, 47, 47, 0.04)',
                },
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Delete Challenge
            </Button>
            <Button
              onClick={handleCloseChallengeDialog}
              variant="contained"
              sx={{
                backgroundColor: '#8D7B68',
                '&:hover': { backgroundColor: '#7A6859' },
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Congratulations Modal */}
        <Modal open={showCongratsModal} onClose={handleCloseCongratsModal}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: '#fff',
              boxShadow: 24,
              p: 4,
              borderRadius: 3,
              textAlign: 'center',
              maxWidth: 400,
              border: '3px solid #8D7B68',
            }}
          >
            <Celebration sx={{ fontSize: 64, color: '#8D7B68', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#333' }}>
              Congratulations!
            </Typography>
            <Typography variant="body1" sx={{ color: '#555', mb: 2 }}>
              You've completed all books in this challenge!
            </Typography>
            <Lottie
              loop={true}
              animationData={congratsAnimation}
              play
              style={{ width: 150, height: 150, margin: 'auto' }}
            />
            <Button
              variant="contained"
              onClick={handleCloseCongratsModal}
              sx={{
                mt: 2,
                backgroundColor: '#8D7B68',
                '&:hover': { backgroundColor: '#7A6859' },
                textTransform: 'none',
                px: 4,
              }}
            >
              Awesome!
            </Button>
          </Box>
        </Modal>
      </Container>
    </Box>
  );
}

export default BookList;
